/**
 * Header Menu - Implementare cu cele mai bune practici pentru accesibilitate și performanță
 * Respectă standardele WCAG 2.1 AA și European Accessibility Act (EAA)
 */

// Configurare globală
const HEADER_CONFIG = {
  BREAKPOINT_DESKTOP: 991,
  DEBOUNCE_DELAY: 20,
  FOCUS_DELAY: 100,
  KEYBOARD_KEYS: {
    ENTER: 'Enter',
    SPACE: ' ',
    ESCAPE: 'Escape',
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_RIGHT: 'ArrowRight',
    TAB: 'Tab'
  }
};

// Clasă pentru gestionarea anunțurilor live către screen reader-e
class LiveAnnouncer {
  constructor() {
    this.liveRegion = this.createLiveRegion();
  }

  createLiveRegion() {
    // V2: refolosim regiunea existentă la re-init (fără duplicate)
    const existing = document.getElementById('live-announcer');
    if (existing) return existing;
    const region = document.createElement('div');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.className = 'visually-hidden';
    region.id = 'live-announcer';
    document.body.appendChild(region);
    return region;
  }

  announce(message, priority = 'polite') {
    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;
    
    // Curățăm mesajul după un scurt timp
    setTimeout(() => {
      this.liveRegion.textContent = '';
    }, 1000);
  }
}

// Clasă pentru gestionarea dropdown-urilor accesibile
class AccessibleDropdown {
  constructor(element, options = {}) {
    this.element = element;
    this.trigger = element.querySelector('.link');
    this.dropdown = element.querySelector('.dropdown');
    this.items = element.querySelectorAll('.item');
    this.isOpen = false;
    this.currentFocus = -1;
    this.options = { ...options };
    
    this.init();
  }

  init() {
    if (!this.trigger || !this.dropdown) return;
    // V2: idempotență per NOD — un element deja legat nu se re-leagă (anti-duplicare);
    // un nav ÎNLOCUIT vine cu noduri curate → se leagă normal.
    if (this.element.dataset.dcMenuBound) return;
    this.element.dataset.dcMenuBound = '1';
    
    // Setez atributele ARIA inițiale
    this.setupAriaAttributes();
    
    // Adaug event listeners
    this.addEventListeners();
  }

  setupAriaAttributes() {
    const dropdownId = this.dropdown.id || `dropdown-${Math.random().toString(36).substr(2, 9)}`;
    this.dropdown.id = dropdownId;
    
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', dropdownId);
    this.trigger.setAttribute('aria-haspopup', 'menu');
    
    this.dropdown.setAttribute('role', 'menu');
    this.dropdown.setAttribute('aria-hidden', 'true');
    
    // Setez role pentru items
    this.items.forEach(item => {
      const link = item.querySelector('a') || item.querySelector('.link');
      if (link) {
        link.setAttribute('role', 'menuitem');
        link.setAttribute('tabindex', '-1');
      }
    });
  }

  addEventListeners() {
    // Hover pentru desktop
    this.element.addEventListener('mouseenter', (e) => {
      if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
        this.open();
      }
    });

    this.element.addEventListener('mouseleave', (e) => {
      if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
        this.close();
      }
    });

    // Click pentru mobil
    // this.trigger.addEventListener('click', (e) => {
    //   if (window.innerWidth <= HEADER_CONFIG.BREAKPOINT_DESKTOP) {
    //     e.preventDefault();
    //     this.toggle();
    //   }
    // });

    
    // Click pentru mobil - pe întregul element pentru a include și săgeata
    this.element.addEventListener('click', (e) => {
      if (window.innerWidth <= HEADER_CONFIG.BREAKPOINT_DESKTOP) {
        // Verificăm dacă click-ul este pe trigger sau pe icon-arrow
        const clickedOnTrigger = e.target.closest('.link') === this.trigger;
        const clickedOnArrow = e.target.classList.contains('icon-arrow');

        if (clickedOnTrigger || clickedOnArrow) {
          e.preventDefault();
          this.toggle();
        }
      }
    });

    // Navigare cu tastatura
    this.trigger.addEventListener('keydown', (e) => this.handleKeyDown(e));
    this.dropdown.addEventListener('keydown', (e) => this.handleDropdownKeyDown(e));
  }

  handleKeyDown(e) {
    switch (e.key) {
      case HEADER_CONFIG.KEYBOARD_KEYS.ENTER:
      case HEADER_CONFIG.KEYBOARD_KEYS.SPACE:
        e.preventDefault();
        this.toggle();
        if (this.isOpen) {
          this.focusFirstItem();
        }
        break;
      case HEADER_CONFIG.KEYBOARD_KEYS.ARROW_DOWN:
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        }
        this.focusFirstItem();
        break;
      case HEADER_CONFIG.KEYBOARD_KEYS.ESCAPE:
        if (this.isOpen) {
          this.close();
          this.trigger.focus();
        }
        break;
    }
  }

  handleDropdownKeyDown(e) {
    const focusableItems = this.getFocusableItems();
    
    switch (e.key) {
      case HEADER_CONFIG.KEYBOARD_KEYS.ARROW_DOWN:
        e.preventDefault();
        this.focusNextItem(focusableItems);
        break;
      case HEADER_CONFIG.KEYBOARD_KEYS.ARROW_UP:
        e.preventDefault();
        this.focusPreviousItem(focusableItems);
        break;
      case HEADER_CONFIG.KEYBOARD_KEYS.ESCAPE:
        e.preventDefault();
        this.close();
        this.trigger.focus();
        break;
      case HEADER_CONFIG.KEYBOARD_KEYS.TAB:
        // Permitem Tab-ul să funcționeze normal, dar închidem dropdown-ul
        this.close();
        break;
    }
  }

  getFocusableItems() {
    return Array.from(this.dropdown.querySelectorAll('a, button, [tabindex="0"]'))
      .filter(item => !item.hasAttribute('disabled') && item.offsetParent !== null);
  }

  focusFirstItem() {
    const focusableItems = this.getFocusableItems();
    if (focusableItems.length > 0) {
      this.currentFocus = 0;
      focusableItems[0].focus();
    }
  }

  focusNextItem(focusableItems) {
    this.currentFocus = (this.currentFocus + 1) % focusableItems.length;
    focusableItems[this.currentFocus].focus();
  }

  focusPreviousItem(focusableItems) {
    this.currentFocus = this.currentFocus <= 0 ? focusableItems.length - 1 : this.currentFocus - 1;
    focusableItems[this.currentFocus].focus();
  }

  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    this.dropdown.classList.add('display');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.dropdown.setAttribute('aria-hidden', 'false');

    // Adăug clasa 'up' pe icon-arrow
    const arrow = this.element.querySelector('.icon-arrow');
    arrow?.classList.add('up');
    
    // Anunț deschiderea pentru screen reader-e
    if (window.liveAnnouncer) {
      const menuName = this.trigger.textContent.trim();
      window.liveAnnouncer.announce(`Meniu ${menuName} deschis`);
    }
    
    // Adăug overlay pentru desktop
    if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
      document.querySelector('.overlay')?.classList.add('show-overlay');
    }
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.dropdown.classList.remove('display');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.dropdown.setAttribute('aria-hidden', 'true');
    this.currentFocus = -1;

    // Elimin clasa 'up' de pe icon-arrow
    const arrow = this.element.querySelector('.icon-arrow');
    arrow?.classList.remove('up');
    
    // Elimin overlay pentru desktop
    if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
      document.querySelector('.overlay')?.classList.remove('show-overlay');
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Funcția principală de inițializare
function startHeader() {
  // Inițializez anunțătorul live
  window.liveAnnouncer = new LiveAnnouncer();
  
  
  // Afișez pagina curentă
  showCurrent();
  
  // Obțin elementele
  const elements = getElements();
  
  // Inițializez dropdown-urile accesibile
  initializeDropdowns(elements);
  
  // Setez layout-ul inițial
  setInitialLayout();
  
  // Adaug event listeners pentru burger menu
  setupMobileMenu(elements);
  
  // Adaug skip link functionality
  setupSkipLink();
  
  // Observ resize-ul ferestrei
  observeResize();
}

function getElements() {
  return {
    buttons: document.querySelectorAll('.menu-element'),
    dropdowns: document.querySelectorAll('header .dropdown'),
    subButtons: document.querySelectorAll('.menu-element:not(.grid) .item'),
    overlay: document.querySelector('.overlay'),
    burger: document.querySelector('.mobile-nav-burger'),
    websiteWrapper: document.querySelector('.website-wrapper'),
    primaryNav: document.querySelector('.primary-nav-wrapper'),
    header: document.querySelector('header')
  };
}

function initializeDropdowns(elements) {
  // Inițializez dropdown-urile cu clasa AccessibleDropdown
  elements.buttons.forEach(button => {
    if (button.querySelector('.dropdown')) {
      new AccessibleDropdown(button);
    }
  });
  
  // Inițializez dropdown-urile de nivel 2
  elements.subButtons.forEach(subButton => {
    if (subButton.querySelector('.secondary-dropdown')) {
      initializeSecondaryDropdown(subButton);
    }
  });
}

function initializeSecondaryDropdown(element) {
  if (element.dataset.dcMenuBound2) return; // V2: idempotență per nod
  element.dataset.dcMenuBound2 = '1';
  const trigger = element.querySelector('.link');
  const dropdown = element.querySelector('.secondary-dropdown');
  
  if (!trigger || !dropdown) return;
  
  // Setez atributele ARIA
  const dropdownId = `secondary-dropdown-${Math.random().toString(36).substr(2, 9)}`;
  dropdown.id = dropdownId;
  
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', dropdownId);
  trigger.setAttribute('aria-haspopup', 'menu');
  
  // Event listeners pentru mobil
  // trigger.addEventListener('click', (e) => {
  //   if (window.innerWidth <= HEADER_CONFIG.BREAKPOINT_DESKTOP) {
  //     e.preventDefault();
  //     toggleSecondaryDropdown(element);
  //   }
  // });

  // Event listeners pentru mobil - pe întregul element pentru a include și săgeata
  element.addEventListener('click', (e) => {
    if (window.innerWidth <= HEADER_CONFIG.BREAKPOINT_DESKTOP) {
      // Verificăm dacă click-ul este pe trigger sau pe icon-arrow
      const clickedOnTrigger = e.target.closest('.link') === trigger;
      const clickedOnArrow = e.target.classList.contains('icon-arrow');

      if (clickedOnTrigger || clickedOnArrow) {
        e.preventDefault();
        e.stopPropagation(); // Opresc propagarea către dropdown-ul părinte
        toggleSecondaryDropdown(element);
      }
    }
  });

  
  // Navigare cu tastatura
  trigger.addEventListener('keydown', (e) => {
    if (e.key === HEADER_CONFIG.KEYBOARD_KEYS.ENTER || e.key === HEADER_CONFIG.KEYBOARD_KEYS.SPACE) {
      e.preventDefault();
      toggleSecondaryDropdown(element);
    }
  });
}

function toggleSecondaryDropdown(element) {
  const dropdown = element.querySelector('.secondary-dropdown');
  const trigger = element.querySelector('.link');
  const arrow = element.querySelector('.icon-arrow');
  
  const isOpen = dropdown.classList.contains('display');
  
  if (isOpen) {
    dropdown.classList.remove('display');
    arrow?.classList.remove('up');
    trigger.setAttribute('aria-expanded', 'false');
  } else {
    dropdown.classList.add('display');
    arrow?.classList.add('up');
    trigger.setAttribute('aria-expanded', 'true');
  }
}

function setInitialLayout() {
  if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
    setDesktop();
  } else {
    setMobile();
  }
}

function setupMobileMenu(elements) {
  if (!elements.burger || elements.burger.dataset.dcMenuBound) return; // V2
  elements.burger.dataset.dcMenuBound = '1';
  elements.burger.addEventListener('click', () => {
    openMobileMenu(elements);
  });
  
  elements.burger.addEventListener('keydown', (e) => {
    if (e.key === HEADER_CONFIG.KEYBOARD_KEYS.ENTER || e.key === HEADER_CONFIG.KEYBOARD_KEYS.SPACE) {
      e.preventDefault();
      openMobileMenu(elements);
    }
  });
  
  elements.overlay.addEventListener('click', () => {
    if (window.innerWidth <= HEADER_CONFIG.BREAKPOINT_DESKTOP) {
      closeMobileMenu(elements);
    }
  });
  
  // Închidere cu Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === HEADER_CONFIG.KEYBOARD_KEYS.ESCAPE && 
        elements.primaryNav.classList.contains('display')) {
      closeMobileMenu(elements);
    }
  });
}

function openMobileMenu(elements) {
  elements.overlay.classList.add('show-overlay');
  elements.websiteWrapper.style.pointerEvents = 'none';
  document.body.style.overflow = 'hidden';
  elements.primaryNav.classList.add('display');
  
  // Focus pe primul element din meniu
  setTimeout(() => {
    const firstMenuItem = elements.primaryNav.querySelector('a, button');
    firstMenuItem?.focus();
  }, HEADER_CONFIG.FOCUS_DELAY);
  
  // Anunț deschiderea meniului
  window.liveAnnouncer.announce('Meniu principal deschis');
}

function closeMobileMenu(elements) {
  elements.primaryNav.classList.remove('display');
  elements.overlay.classList.remove('show-overlay');
  elements.websiteWrapper.style.pointerEvents = 'unset';
  document.body.style.overflow = 'auto';
  
  // Retur focus-ul la meniul burger
  elements.burger.focus();
}

function setupSkipLink() {
  const skipLink = document.querySelector('.skip-link');
  if (skipLink) {
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(skipLink.getAttribute('href'));
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }
}

function setDesktop() {
  const elements = getElements();
  elements.primaryNav.style.height = '';
  elements.primaryNav.style.position = 'relative';
  
  const windowWidth = document.body.clientWidth;
  const padding = Math.max(0, (windowWidth - 1600) / 2);
  
  // Optimizez pozițiile dropdown-urilor
  elements.dropdowns.forEach(dropdown => {
    dropdown.style.left = '0';
    const thisWidth = dropdown.offsetWidth;
    const rightPosition = dropdown.getBoundingClientRect().right;
    
    if (window.innerWidth > 1600 && rightPosition > windowWidth - padding) {
      const newPosition = rightPosition - 1600 - padding;
      dropdown.style.left = `-${newPosition}px`;
    } else if (window.innerWidth <= 1600 && rightPosition > windowWidth) {
      const newPosition = rightPosition - windowWidth + 20;
      dropdown.style.left = `-${newPosition}px`;
    }
  });
}

function setMobile() {
  const elements = getElements();
  elements.dropdowns.forEach(dropdown => {
    dropdown.style.left = '0';
  });
  
  elements.primaryNav.style.height = `${window.innerHeight}px`;
  elements.primaryNav.style.position = 'fixed';
}

function observeResize() {
  // V2: un singur observer, indiferent de câte re-init-uri (anti-acumulare)
  if (window.__dcMenuResizeObs) return;
  window.__dcMenuResizeObs = true;
  let timeOut;
  
  const resetHeader = () => {
    clearTimeout(timeOut);
    timeOut = setTimeout(() => {
      if (window.innerWidth > HEADER_CONFIG.BREAKPOINT_DESKTOP) {
        setDesktop();
      } else {
        setMobile();
      }
    }, HEADER_CONFIG.DEBOUNCE_DELAY);
  };
  
  new ResizeObserver(resetHeader).observe(document.body);
}

function showCurrent() {
  const currentHref = window.location.href.replace("https://mobexpert.ro", "");
  const currentButtons = document.querySelectorAll(`header [href="${currentHref}"]`);
  
  currentButtons.forEach(button => {
    button.classList.add('active');
    button.setAttribute('aria-current', 'page');
  });
  
  // Anunț pagina curentă pentru screen reader-e
  if (currentButtons.length > 0 && window.liveAnnouncer) {
    const currentPageName = currentButtons[0].textContent.trim();
    window.liveAnnouncer.announce(`Pagina curentă: ${currentPageName}`);
  }
}

// Inițializare cu debounce pentru performanță
let initTimeout;
function debouncedInit() {
  clearTimeout(initTimeout);
  initTimeout = setTimeout(startHeader, 10);
}

// Event listener pentru DOMContentLoaded
document.addEventListener('DOMContentLoaded', debouncedInit, { once: true });

// ── V2 (2026-08-09): AUTO-VINDECARE ──────────────────────────────────────────
// Diagnoză dovedită pe /cart: la acțiunile de coș, nav.primary-nav este ÎNLOCUIT
// integral în DOM de un script terț (identitate schimbată, listeneri pierduți) →
// meniul murea până la reload. Reinițializăm idempotent (gardurile per-nod de mai
// sus fac re-init-ul sigur): la evenimentele fluxului de coș + un observer ieftin
// pe body care detectează un nav fără marcaj de legare.
document.addEventListener('dc:cart-refreshed', debouncedInit);
document.addEventListener('dc:cart-mutated', debouncedInit);
(function () {
  let t = null;
  try {
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () {
        const nav = document.querySelector('nav.primary-nav');
        if (nav && !nav.querySelector('[data-dc-menu-bound]')) debouncedInit();
      }, 300);
    }).observe(document.body, { childList: true, subtree: true });
  } catch (e) { /* fail-soft: rămân măcar evenimentele de coș */ }
})();

// Export pentru testare (dacă este necesar)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { 
    startHeader, 
    AccessibleDropdown, 
    LiveAnnouncer,
    HEADER_CONFIG 
  };
}